/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-13 11:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { Field } from 'redux-form';
import { Collapse } from 'react-collapse';
import { CaretDownMinor, ExternalMinor } from '@shopify/polaris-icons';
import TextForm from './text-form';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { nonXSSv2 } from 'src/helpers/form-validation-helper';
import { checkHasXss } from 'src/helpers';
import { URL } from 'src/constants/links';

import {
  PRODUCT_PRICE_DISPLAY_STYLE_OPTIONS,
  PRODUCT_COMPARE_AT_PRICE_POSITION_OPTIONS
} from 'src/constants/theme-settings';
import { DropdownIndicator, styles } from './react-select-style';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();

class ProductPriceForm extends Component<ComposedProps> {
  state = {
    isOpenPrice: true,
    isOpenSalePrice: true,
    isOpenCompareAtPrice: true,
    isOpenSavedDisplay: true
  };

  handleToggleCollapse = name => {
    this.setState({
      [name]: !this.state[name]
    });
  };

  getSavingDisplayText = () => {
    const { i18n } = this.props;
    let text = '';
    switch (i18n.locale) {
      case 'en':
        text = `${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled1'
        )} {{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled2'
        )}}}`;
        break;
      case 'de':
        text = `{{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled2'
        )}}} ${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled1'
        )} `;
        break;
      default:
        break;
    }
    return text;
  };

  handleUnmountInput = () => {
    const { formValues, prefix, updateField } = this.props;
    const text = _.get(formValues, `${prefix}.savingDisplayText`, '');
    if (checkHasXss(text)) {
      updateField(`${prefix}.savingDisplayText`, '');
    }
  };

  render() {
    const { i18n, prefix, formValues, labelTranslation, updateField } = this.props;
    const { isOpenPrice, isOpenSalePrice, isOpenCompareAtPrice, isOpenSavedDisplay } = this.state;
    const savedDisplayColor = _.get(formValues, `${prefix}.savingDisplayColor`, '');
    const showSavingDisplay = _.get(formValues, `${prefix}.showSavingDisplay`, false);

    const formattedDisplayStyle = PRODUCT_PRICE_DISPLAY_STYLE_OPTIONS.map(item => {
      const label = item.format
        ? item.format
            .replace('label', i18n.translate(item.label))
            .replace('label2', i18n.translate(item.label2))
        : i18n.translate(item.label);
      return {
        ...item,
        label: label
      };
    });

    const formattedCompareAtPricePosition = PRODUCT_COMPARE_AT_PRICE_POSITION_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.productPrice_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['ProductPriceThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          {/* Show cents as superscript */}
          <div className="form-section__item">
            <Field
              name={`${prefix}.showCentAsSuperscript`}
              component={RenderCheckbox}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.productPrice_ShowSuperscript')}
            />
          </div>

          {/* Show cents as superscript */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.showCurrencyCodes`}
              component={RenderCheckbox}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.productPrice_ShowCurrencyCodes')}
            />
          </div>

          {/* Display style */}
          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`${prefix}.showMultiVariantPrice`}
              component={RenderSelect2}
              options={formattedDisplayStyle}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.productPrice_DisplayStyle_Title')}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />

            <div style={{ marginTop: '12px' }}>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.ThemeSetting.Form.productPrice_DisplayStyle_Note')}
              </TextStyle>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          {/* Price */}
          <div className="form-section form-section--bordered">
            <div
              className="section-title-collapse__wrapper"
              onClick={() => this.handleToggleCollapse('isOpenPrice')}>
              <span className="section-title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.price_Title')}
              </span>
              <span className={`caret ${!isOpenPrice ? 'closed' : ''}`}>
                <Icon source={CaretDownMinor} color="base" />
              </span>
            </div>

            <Collapse isOpened={isOpenPrice}>
              <div className="form-section__item">
                <TextForm
                  formValues={formValues}
                  updateField={updateField}
                  prefix={`${prefix}.price`}
                  labelColor={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productPrice_PriceColor_Item'
                  )}
                  showColor
                  titleSmall
                  customFieldNameColor={`${prefix}.priceColor`}
                  customFieldNameSize={`${prefix}.priceFontSize`}
                  customFieldNameStyle={`${prefix}.priceFontStyle`}
                  customFieldNameFamily={`${prefix}.priceFontFamily`}
                />
              </div>
            </Collapse>
          </div>

          {/* Sale price */}
          <div className="form-section form-section--bordered">
            <div
              className="section-title-collapse__wrapper"
              onClick={() => this.handleToggleCollapse('isOpenSalePrice')}>
              <span className="section-title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.salePrice_Title')}
              </span>
              <span className={`caret ${!isOpenSalePrice ? 'closed' : ''}`}>
                <Icon source={CaretDownMinor} color="base" />
              </span>
            </div>

            <Collapse isOpened={isOpenSalePrice}>
              <div className="form-section__item">
                <TextForm
                  formValues={formValues}
                  updateField={updateField}
                  prefix={`${prefix}.salePrice`}
                  labelColor={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productPrice_SalePriceColor_Item'
                  )}
                  showColor
                  titleSmall
                  customFieldNameColor={`${prefix}.salePriceColor`}
                  customFieldNameSize={`${prefix}.salePriceFontSize`}
                  customFieldNameStyle={`${prefix}.salePriceFontStyle`}
                  customFieldNameFamily={`${prefix}.salePriceFontFamily`}
                />
              </div>
            </Collapse>
          </div>

          {/* Compare At Price */}
          <div className="form-section form-section--bordered">
            <div
              className="section-title-collapse__wrapper"
              onClick={() => this.handleToggleCollapse('isOpenCompareAtPrice')}>
              <span className="section-title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.compareAtPrice_Title')}
              </span>
              <span className={`caret ${!isOpenCompareAtPrice ? 'closed' : ''}`}>
                <Icon source={CaretDownMinor} color="base" />
              </span>
            </div>

            <Collapse isOpened={isOpenCompareAtPrice}>
              {/* Compare at price position */}
              <div className="form-section__item">
                <Field
                  name={`${prefix}.compareAtPricePosition`}
                  component={RenderSelect2}
                  options={formattedCompareAtPricePosition}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productPrice_CompareAtPricePosition_Item'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                />
              </div>

              <div className="form-section__item form-section__item--medium-spacing">
                <TextForm
                  formValues={formValues}
                  updateField={updateField}
                  prefix={`${prefix}.compareAtPrice`}
                  labelColor={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productPrice_CompareAtPriceColor_Item'
                  )}
                  showColor
                  titleSmall
                  customFieldNameColor={`${prefix}.compareAtPriceColor`}
                  customFieldNameSize={`${prefix}.compareAtPriceFontSize`}
                  customFieldNameStyle={`${prefix}.compareAtPriceFontStyle`}
                  customFieldNameFamily={`${prefix}.compareAtPriceFontFamily`}
                />
              </div>
            </Collapse>
          </div>

          {/* Saved display */}
          <div className="form-section form-section--bordered">
            <div
              className="section-title-collapse__wrapper"
              onClick={() => this.handleToggleCollapse('isOpenSavedDisplay')}>
              <span className="section-title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.savedDisplay_Title')}
              </span>
              <span className={`caret ${!isOpenSavedDisplay ? 'closed' : ''}`}>
                <Icon source={CaretDownMinor} color="base" />
              </span>
            </div>

            <Collapse isOpened={isOpenSavedDisplay}>
              {/* Show saving display */}
              <div className="form-section__item">
                <Field
                  name={`${prefix}.showSavingDisplay`}
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productPrice_ShowSavedDisplay'
                  )}
                />
              </div>

              {showSavingDisplay && (
                <>
                  {/* Savings display text */}
                  <div className="form-section__item form-section__item--standard-spacing">
                    <Field
                      name={`${prefix}.savingDisplayText`}
                      component={RenderTextField}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText'
                      )}
                      placeholder={
                        labelTranslation?.savingAmountV2?.value || this.getSavingDisplayText()
                      }
                      validate={[nonXSSValue]}
                      onUnmount={this.handleUnmountInput}
                    />

                    <div style={{ marginTop: '12px' }}>
                      <TextStyle variation="subdued">
                        {`{{${i18n.translate(
                          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Note1_1'
                        )}}}${i18n.translate(
                          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Note1_2'
                        )}`}
                      </TextStyle>
                      <br />
                      <TextStyle variation="subdued">
                        {`{{${i18n.translate(
                          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Note2_1'
                        )}}}${i18n.translate(
                          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Note2_2'
                        )}`}
                      </TextStyle>
                    </div>
                  </div>

                  {/* Saving display color */}
                  <div className="form-section__item form-section__item form-section__item--medium-spacing">
                    <p className="title title--small">
                      {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                    </p>
                    <RenderColorPickerItem
                      colorValue={savedDisplayColor}
                      fieldName={`${prefix}.savingDisplayColor`}
                      fieldLabel={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayColor_Item'
                      )}
                      updateField={updateField}
                    />
                  </div>
                </>
              )}
            </Collapse>
          </div>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductPriceForm);
